import { Capacitor, registerPlugin } from "@capacitor/core";
import { App } from "@capacitor/app";
import { Device } from "@capacitor/device";
import { PushNotifications } from "@capacitor/push-notifications";
import backofficeService from "@/api/backoffice";
import { isNativeMobileApp } from "./runtime";
import { mobileBrand } from "./brand";

const installationKey = `${process.env.VUE_APP_STORAGE_PREFIX}mobileInstallationId`;
const registeredKey = `${process.env.VUE_APP_STORAGE_PREFIX}mobilePushRegistered`;
const notificationHistoryKey = `${process.env.VUE_APP_STORAGE_PREFIX}mobileNotificationHistory`;
const pendingNotificationInboxKey = `${process.env.VUE_APP_STORAGE_PREFIX}pendingNotificationInbox`;
const notificationHistoryLimit = 200;
const pendingNotificationInboxLifetime = 5 * 60 * 1000;
let listenersInitialized = false;
let listenersInitializationPromise = null;
let registrationPromise = null;
let registrationOrganizationId = null;
let resolveRegistration;
let rejectRegistration;
let registrationTimeout;
let moneyAlertChannelPromise = null;
let mobileRouter = null;
let notificationNavigationInstalled = false;
const PushConfiguration = registerPlugin("PushConfiguration");
const repairNestMoneyAlertChannel = {
  id: "repairnest_money_alerts_v1",
  name: "Money alerts",
  description: "Register, sales, and other money activity",
  importance: 5,
  visibility: 1,
  sound: "repairnest_kaching.wav",
  vibration: true,
  lights: true,
};

async function isNativePushConfigured() {
  if (!isNativeMobileApp || Capacitor.getPlatform() !== "android") return isNativeMobileApp;
  if (!Capacitor.isPluginAvailable("PushConfiguration")) return false;
  try {
    const { configured } = await PushConfiguration.getStatus();
    return configured === true;
  } catch (error) {
    return false;
  }
}

function hasPendingNotificationInbox() {
  const requestedAt = Number(localStorage.getItem(pendingNotificationInboxKey));
  if (Number.isFinite(requestedAt) && requestedAt > 0 && Date.now() - requestedAt <= pendingNotificationInboxLifetime) return true;
  localStorage.removeItem(pendingNotificationInboxKey);
  return false;
}

function finishNotificationInboxNavigation() {
  localStorage.removeItem(pendingNotificationInboxKey);
  window.dispatchEvent(new CustomEvent("repairnest-notification-opened"));
}

function openNotificationInbox() {
  localStorage.setItem(pendingNotificationInboxKey, String(Date.now()));
  if (!mobileRouter) return;
  const navigate = () => {
    if (!hasPendingNotificationInbox() || !localStorage.getItem(`${process.env.VUE_APP_STORAGE_PREFIX}token`)) return;
    if (mobileRouter.currentRoute.path === "/notifications") {
      finishNotificationInboxNavigation();
      return;
    }
    mobileRouter.replace("/notifications").catch(() => {});
  };
  mobileRouter.onReady(navigate);
  navigate();
}

export function initializeMobileNotificationNavigation(router) {
  mobileRouter = router;
  if (!mobileRouter || notificationNavigationInstalled) return;
  notificationNavigationInstalled = true;
  mobileRouter.afterEach((to) => {
    if (!hasPendingNotificationInbox()) return;
    if (to.path === "/notifications") {
      finishNotificationInboxNavigation();
      return;
    }
    if (localStorage.getItem(`${process.env.VUE_APP_STORAGE_PREFIX}token`)) {
      setTimeout(() => mobileRouter.replace("/notifications").catch(() => {}), 0);
    }
  });
}

function navigateFromPayload(payload = {}, { defaultToInbox = false } = {}) {
  const action = mobileNotificationAction(payload);
  if (defaultToInbox) {
    openNotificationInbox();
    return;
  }
  const path = action.externalUrl ? "/notifications" : action.path;
  if (mobileRouter && path && path !== mobileRouter.currentRoute.fullPath) {
    mobileRouter.push(path).catch(() => {});
  }
}

export function mobileNotificationAction(notification = {}) {
  const data = notification.data || {};
  const actionType = String(notification.actionType || data.actionType || "").trim().toLowerCase();
  const eventType = String(notification.type || data.type || "").trim().toLowerCase();
  const entityType = String(notification.entityType || data.entityType || "").trim().toLowerCase();
  const sharedLinkCode = String(notification.sharedLinkCode || data.sharedLinkCode || "").trim();
  const recordId = String(notification.recordId || data.recordId || "").trim();
  const numericRecordId = Number(recordId);
  if (eventType === "invoice_deleted" && actionType === "shared_receipt" && entityType === "invoice" && /^[A-HJ-NP-Za-km-z2-9]{10}$/.test(sharedLinkCode)) {
    return {
      actionType,
      entityType,
      sharedLinkCode,
      label: "View invoice in browser",
      externalUrl: `${mobileBrand.publicOrigin}/d/${encodeURIComponent(sharedLinkCode)}`,
    };
  }
  if (!/^\d+$/.test(recordId) || !Number.isSafeInteger(numericRecordId) || numericRecordId < 1) return {};
  if (eventType === "invoice_deleted" && actionType === "invoice_receipt" && entityType === "invoice") {
    return { actionType, entityType, recordId, label: "View invoice", path: `/invoice/list?receiptId=${encodeURIComponent(recordId)}` };
  }
  const locationId = String(notification.locationId || data.locationId || "").trim();
  const numericLocationId = Number(locationId);
  if (actionType === "shift_summary" && /^\d+$/.test(locationId) && Number.isSafeInteger(numericLocationId) && numericLocationId > 0) {
    return { actionType, recordId, label: "View shift summary", path: `/mobile-actions/shift/${encodeURIComponent(recordId)}?locationId=${encodeURIComponent(locationId)}` };
  }
  return {};
}

function safeNotificationRecord(notification = {}) {
  const data = notification.data || {};
  const route = data.path || data.route || data.url || notification.route || "";
  const receivedAt = notification.createdAt || notification.receivedAt || data.createdAt || new Date().toISOString();
  const title = String(notification.title || `${mobileBrand.name} notification`).slice(0, 140);
  const body = String(notification.body || "");
  const organization = typeof (data.organizationName || data.organization || notification.organization) === "string"
    ? (data.organizationName || data.organization || notification.organization).slice(0, 120)
    : "";
  const location = typeof (data.locationName || data.location || notification.location) === "string"
    ? (data.locationName || data.location || notification.location).slice(0, 120)
    : "";
  const portalHost = new URL(process.env.VUE_APP_MOBILE_PORTAL_ORIGIN || mobileBrand.portalOrigin).host;
  const safeRoute = typeof route === "string" && (route.startsWith("/") || (() => {
    try { return new URL(route).protocol === "https:" && new URL(route).host === portalHost; } catch (error) { return false; }
  })())
    ? route.slice(0, 500)
    : "";
  const id = String(data.notificationId || notification.id || `${title}|${body}|${safeRoute}|${receivedAt.slice(0, 16)}`).slice(0, 600);
  const iconKey = typeof (data.iconKey || notification.iconKey) === "string"
    ? (data.iconKey || notification.iconKey).slice(0, 80)
    : "";
  const action = mobileNotificationAction(notification);
  return {
    id,
    type: String(notification.type || data.type || "").slice(0, 80),
    iconKey,
    actionType: action.actionType || "",
    entityType: action.entityType || "",
    recordId: action.recordId || "",
    sharedLinkCode: action.sharedLinkCode || "",
    title,
    body,
    organization,
    location,
    locationId: notification.locationId || data.locationId || null,
    route: safeRoute,
    readAt: notification.readAt || null,
    receivedAt,
  };
}

function saveNotification(notification) {
  if (!localStorage.getItem(`${process.env.VUE_APP_STORAGE_PREFIX}token`)) return null;
  const record = safeNotificationRecord(notification);
  let history = [];
  try {
    history = JSON.parse(localStorage.getItem(notificationHistoryKey) || "[]");
    if (!Array.isArray(history)) history = [];
  } catch (error) {
    history = [];
  }
  const existing = history.find((item) => item && item.id === record.id);
  if (existing && existing.receivedAt) record.receivedAt = existing.receivedAt;
  history = [record, ...history.filter((item) => item && item.id !== record.id)].slice(0, notificationHistoryLimit);
  localStorage.setItem(notificationHistoryKey, JSON.stringify(history));
  window.dispatchEvent(new CustomEvent("repairnest-notification-history-changed", { detail: { source: "push" } }));
  return record;
}

export function getMobileNotificationHistory() {
  try {
    const history = JSON.parse(localStorage.getItem(notificationHistoryKey) || "[]");
    return Array.isArray(history) ? history.slice(0, notificationHistoryLimit) : [];
  } catch (error) {
    return [];
  }
}

export function cacheMobileNotificationHistory(notifications = []) {
  const cached = getMobileNotificationHistory();
  const merged = notifications
    .map((notification) => safeNotificationRecord(notification))
    .concat(cached)
    .filter((notification, index, all) => all.findIndex((item) => item.id === notification.id) === index)
    .sort((left, right) => new Date(right.receivedAt) - new Date(left.receivedAt))
    .slice(0, notificationHistoryLimit);
  localStorage.setItem(notificationHistoryKey, JSON.stringify(merged));
  window.dispatchEvent(new CustomEvent("repairnest-notification-history-changed", { detail: { source: "server" } }));
  return merged;
}

export async function getServerNotificationHistory(params = {}) {
  const response = await backofficeService.Api().get("mobile/notifications", { params });
  const payload = response.data || {};
  return {
    items: Array.isArray(payload.data) ? payload.data.map((item) => safeNotificationRecord(item)) : [],
    count: Number(payload.count) || 0,
    unreadCount: Number(payload.unreadCount) || 0,
    limit: Number(payload.limit) || Number(params.limit) || 20,
    offset: Number(payload.offset) || 0,
  };
}

export async function markServerNotificationRead(id) {
  if (!id) return null;
  const response = await backofficeService.Api().patch(`mobile/notifications/${encodeURIComponent(id)}/read`);
  return response.data && response.data.data ? safeNotificationRecord(response.data.data) : null;
}

const mobilePreferenceKeys = new Set(["shift-close", "sales-summary", "invoice-delete", "store-open"]);

function assertMobilePreferenceKey(preferenceKey) {
  if (!mobilePreferenceKeys.has(preferenceKey)) throw new Error("Unsupported mobile notification preference");
}

export async function getMobilePushPreference(preferenceKey, locationId) {
  assertMobilePreferenceKey(preferenceKey);
  const response = await backofficeService.Api().get(`mobile/preferences/${encodeURIComponent(preferenceKey)}`, { params: { locationId } });
  return response.data && response.data.data
    ? response.data.data
    : { preferenceKey, locationId, enabled: false };
}

export async function setMobilePushPreference(preferenceKey, locationId, enabled) {
  assertMobilePreferenceKey(preferenceKey);
  const response = await backofficeService.Api().put(`mobile/preferences/${encodeURIComponent(preferenceKey)}`, {
    locationId,
    enabled: Boolean(enabled),
  });
  return response.data && response.data.data ? response.data.data : null;
}

export function clearMobileNotificationHistory() {
  localStorage.removeItem(notificationHistoryKey);
  window.dispatchEvent(new CustomEvent("repairnest-notification-history-changed"));
}

async function getInstallationId() {
  let installationId = localStorage.getItem(installationKey);
  if (!installationId) {
    installationId = (await Device.getId()).identifier;
    localStorage.setItem(installationKey, installationId);
  }
  return installationId;
}

export async function ensureMoneyAlertChannel() {
  if (!isNativeMobileApp || mobileBrand.key !== "repairnest" || Capacitor.getPlatform() !== "android") return;
  if (!moneyAlertChannelPromise) {
    moneyAlertChannelPromise = PushNotifications.createChannel(repairNestMoneyAlertChannel).catch((error) => {
      console.warn("Unable to prepare the money alert notification channel", error); // eslint-disable-line no-console
    });
  }
  return moneyAlertChannelPromise;
}

export async function initializeMobileNotifications() {
  if (!isNativeMobileApp || listenersInitialized) return;
  if (listenersInitializationPromise) return listenersInitializationPromise;
  listenersInitializationPromise = (async () => {
    if (!(await isNativePushConfigured())) return;
    await ensureMoneyAlertChannel();
    await PushNotifications.addListener("registration", async ({ value: pushToken }) => {
      try {
        const [{ version: appVersion }, installationId] = await Promise.all([App.getInfo(), getInstallationId()]);
        await backofficeService.Api().post("mobile/devices", {
          brand: mobileBrand.key,
          installationId,
          pushToken,
          platform: Capacitor.getPlatform(),
          appVersion,
        });
        localStorage.setItem(registeredKey, "true");
        if (resolveRegistration) resolveRegistration();
      } catch (error) {
        localStorage.removeItem(registeredKey);
        if (rejectRegistration) rejectRegistration(error);
        console.error("Unable to save this device for notifications", error); // eslint-disable-line no-console
      }
    });
    await PushNotifications.addListener("registrationError", (error) => {
      localStorage.removeItem(registeredKey);
      if (rejectRegistration) rejectRegistration(error);
      console.error("Push notification registration failed", error); // eslint-disable-line no-console
    });
    await PushNotifications.addListener("pushNotificationReceived", (notification) => {
      saveNotification(notification);
    });
    await PushNotifications.addListener("pushNotificationActionPerformed", ({ notification }) => {
      saveNotification(notification);
      navigateFromPayload(notification, { defaultToInbox: true });
    });
    await App.addListener("appUrlOpen", ({ url }) => navigateFromPayload({ url }));
    listenersInitialized = true;
  })();
  try {
    await listenersInitializationPromise;
  } finally {
    listenersInitializationPromise = null;
  }
}

export async function getMobileNotificationStatus() {
  if (!isNativeMobileApp) return "unsupported";
  if (!(await isNativePushConfigured())) return "unavailable";
  const permission = await PushNotifications.checkPermissions();
  return permission.receive === "granted" && localStorage.getItem(registeredKey) === "true"
    ? "enabled"
    : permission.receive;
}

export async function refreshMobilePushRegistration({ requestPermission = false } = {}) {
  if (!isNativeMobileApp) return "unsupported";
  if (!(await isNativePushConfigured())) return "unavailable";
  const organizationId = localStorage.getItem(`${process.env.VUE_APP_STORAGE_PREFIX}organizationId`);
  if (!localStorage.getItem(`${process.env.VUE_APP_STORAGE_PREFIX}token`) || !organizationId) return "unauthenticated";
  await initializeMobileNotifications();
  let permission = await PushNotifications.checkPermissions();
  if (permission.receive !== "granted" && requestPermission) permission = await PushNotifications.requestPermissions();
  if (permission.receive !== "granted") return permission.receive;
  if (registrationPromise) {
    if (registrationOrganizationId === organizationId) return registrationPromise;
    await registrationPromise.catch(() => {});
    return refreshMobilePushRegistration({ requestPermission });
  }
  registrationOrganizationId = organizationId;
  registrationPromise = new Promise((resolve, reject) => {
    resolveRegistration = () => resolve("enabled");
    rejectRegistration = reject;
    registrationTimeout = setTimeout(() => reject(new Error("Notification registration timed out")), 15000);
  });
  try {
    await PushNotifications.register();
    return await registrationPromise;
  } finally {
    clearTimeout(registrationTimeout);
    registrationTimeout = null;
    resolveRegistration = null;
    rejectRegistration = null;
    registrationPromise = null;
    registrationOrganizationId = null;
  }
}

export async function enableMobileNotifications() {
  return refreshMobilePushRegistration({ requestPermission: true });
}

export async function unregisterMobileDevice() {
  if (!isNativeMobileApp) return;
  const installationId = localStorage.getItem(installationKey);
  if (installationId) {
    await backofficeService.Api().delete(`mobile/devices/${encodeURIComponent(installationId)}`, {
      params: { brand: mobileBrand.key },
    });
  }
  localStorage.removeItem(registeredKey);
}
